import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Image from '../../Utils/Image/image-component';
import SystemDataService from '../../Services/SystemDataService';

export default function WhyChooseUsSection({
    sectionData,
    infoCards: passedInfoCards
}) {
    const { dir } = useSelector((state) => state.systemData || {});
    const { label, titleLine1, titleLine2, subtitle } = sectionData || {};

    const [cards, setCards] = useState([]);
    const [visibleCards, setVisibleCards] = useState([]);

    useEffect(() => {
        async function loadCards() {
            if (passedInfoCards?.length > 0) {
                setCards(passedInfoCards);
                return;
            }

            try {
                const data = await SystemDataService.getCardInfo();
                setCards(data || []);
            } catch (err) {
                console.error('Failed to fetch info cards:', err);
            }
        }

        loadCards();
    }, [passedInfoCards]);

    useEffect(() => {
        if (!cards.length) return;

        setVisibleCards(new Array(cards.length).fill(false));

        const timeouts = cards.map((_, index) =>
            setTimeout(() => {
                setVisibleCards((prev) => {
                    const updated = [...prev];
                    updated[index] = true;
                    return updated;
                });
            }, 150 + index * 120)
        );

        return () => timeouts.forEach(clearTimeout);
    }, [cards]);

    return (
        <section dir={dir} className="w-full bg-gray-50 py-16 md:py-20 lg:py-24 px-5 md:px-6">
            <div className="max-w-5xl mx-auto flex flex-col items-center">

                {label && (
                    <div className="flex flex-col items-center mb-7">
                        <span className="text-secondary-600 text-sm md:text-base font-bold uppercase tracking-[0.22em]">
                            {label}
                        </span>
                        <div className="h-[2px] w-full bg-secondary-600 mt-3" />
                    </div>
                )}

                <div className="text-center mb-5">
                    {titleLine1 && (
                        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-900 leading-[1.05]">
                            {titleLine1}
                        </h2>
                    )}

                    {titleLine2 && (
                        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-secondary-600 leading-[1.05]">
                            {titleLine2}
                        </h2>
                    )}
                </div>

                {subtitle && (
                    <p className="text-center font-bold text-primary-900 uppercase tracking-[0.14em] text-xs md:text-sm mb-12 md:mb-14 mt-3 max-w-3xl">
                        {subtitle}
                    </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-x-12 md:gap-y-10 lg:gap-x-16 lg:gap-y-12 w-full">
                    {cards.map((card, i) => (
                        <div
                            key={card.id || i}
                            className={`
                                group flex items-center gap-5 min-h-[96px]
                                bg-white sm:bg-transparent
                                rounded-3xl sm:rounded-none
                                p-5 sm:p-0
                                shadow-sm sm:shadow-none
                                transition-all duration-700 ease-out
                                ${visibleCards[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                            `}
                        >
                            <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center rounded-2xl transition-transform duration-500 ">
                                <Image
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-full object-contain"
                                    useSekeleton={false}
                                />
                            </div>

                            <div className="pt-1 text-left">
                                <h3 className="text-xl md:text-2xl font-bold text-primary-900 leading-tight">
                                    {card.title}
                                </h3>

                                {card.description && (
                                    <p className="text-gray-400 text-sm md:text-base mt-1 leading-relaxed">
                                        {card.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
}